import { MRT_ColumnDef, useMaterialReactTable } from 'material-react-table';
import { Popover } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FC, useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';
import { format } from 'date-fns';
import { formatNumber } from 'src/utils/utility';
import IconDown from 'src/assets/images/icons/chevron-down.svg';
import type { ContractDto } from 'src/generated/services/TFinancialApi';
import { DiscountDto, useDiscountList } from 'src/use-cases/settings/useDiscounts';
import { useAllServiceProvidersDropdown } from 'src/use-cases/settings/useServiceProvider';
import { useContractsByServiceProvider } from 'src/use-cases/settings/useContracts';
import { useEnhancedTable } from 'src/presentations/hooks/useEnhancedTable';
import { SettingsBaseTable } from 'src/presentations/components/molecules/settings/table/BaseTable';
import { createTableConfig } from 'src/presentations/utils/tableConfigUtils';
import { SettingsMain } from '../SettingsMain';
import { SettingsColumnHeader } from '../SettingsColumnHeader';

const StyledPopover = styled(Popover)({
  '& .MuiPopover-paper': {
    width: '260px',
    boxShadow: 'none',
    backgroundColor: 'transparent',
    paddingTop: '1px',
  },
});

// --- Module-level column renderers (prevents nested component warnings) ---

const DiscountColumnHeader = ({ column }: { column: any }) => (
  <SettingsColumnHeader column={column} onSort={(_, dir) => column.toggleSorting(dir === 'desc')} onFilter={() => {}} />
);

const RevenueRangeCell = ({ row }: { row: any }) => {
  const { revenueRangeMin, revenueRangeMax } = row.original as DiscountDto;
  if (revenueRangeMax === null || revenueRangeMax === undefined) {
    return <>{formatNumber(revenueRangeMin ?? 0)} - Up</>;
  }
  return (
    <>
      {formatNumber(revenueRangeMin ?? 0)} - {formatNumber(revenueRangeMax)}
    </>
  );
};

const DiscountCell = ({ cell }: { cell: any }) => {
  const val = cell.getValue() as number;
  return val !== undefined && val !== null ? <>{val}%</> : <>-</>;
};

// ---

interface SelectedSP {
  id: number;
  label: string;
}

interface DiscountsMainProps {
  searchQuery?: string;
}

export const DiscountsMain: FC<DiscountsMainProps> = (props) => {
  const { searchQuery } = props;

  const [selectedSP, setSelectedSP] = useState<SelectedSP | null>(null);
  const [spMenuEl, setSpMenuEl] = useState<HTMLButtonElement | null>(null);
  const openSPMenu = Boolean(spMenuEl);

  const [selectedContract, setSelectedContract] = useState<ContractDto | null>(null);
  const [validityMenuEl, setValidityMenuEl] = useState<HTMLButtonElement | null>(null);
  const openValidityMenu = Boolean(validityMenuEl);

  const { pagination, onChangePage, onChangePageSize } = useEnhancedTable<DiscountDto>({
    onSearchQueryChange: searchQuery,
  });

  const { data: spData } = useAllServiceProvidersDropdown();
  const spOptions = useMemo(
    () => spData?.data?.content?.filter((sp) => sp.hasDiscount === true).map((sp) => ({ label: sp.code ?? sp.name, id: sp.id! })) ?? [],
    [spData]
  );

  // Auto-select first SP when options load
  useEffect(() => {
    if (!selectedSP && spOptions.length > 0) {
      setSelectedSP({ id: spOptions[0].id, label: spOptions[0].label });
    }
  }, [spOptions.length]);

  const { data: contractsData } = useContractsByServiceProvider(selectedSP?.id);
  const contracts = useMemo(() => contractsData?.data?.content ?? [], [contractsData]);

  // Auto-select most recent contract when SP or contracts change
  useEffect(() => {
    if (!contracts.length) {
      setSelectedContract(null);
      return;
    }
    const mostRecent = [...contracts].sort((a, b) => new Date(b.endDate ?? '').getTime() - new Date(a.endDate ?? '').getTime())[0];
    setSelectedContract(mostRecent);
  }, [contracts.length, selectedSP?.id]);

  const { data: discountsData, isLoading } = useDiscountList(
    selectedContract ? { contractIds: [selectedContract.id!] } : undefined,
    { page: pagination.pageIndex, size: pagination.pageSize },
    { enabled: !!selectedContract }
  );
  const discountsTotal = discountsData?.totalItems || 0;
  const discounts = discountsData?.data ?? [];

  const columns = useMemo(
    (): MRT_ColumnDef<DiscountDto>[] => [
      {
        accessorKey: 'services',
        header: 'Services',
        Header: DiscountColumnHeader,
      },
      {
        accessorKey: 'routeStartWith',
        header: 'Route Start With',
        Header: DiscountColumnHeader,
      },
      {
        id: 'revenueRange',
        header: 'Revenue Range',
        accessorFn: (row) => row.revenueRangeMin ?? 0,
        Header: DiscountColumnHeader,
        Cell: RevenueRangeCell,
      },
      {
        accessorKey: 'discount',
        header: 'Discount',
        Header: DiscountColumnHeader,
        Cell: DiscountCell,
      },
    ],
    []
  );

  const baseTableConfig = createTableConfig<DiscountDto>({});

  const table = useMaterialReactTable({
    ...baseTableConfig,
    columns,
    data: discounts,
    enableRowActions: false,
    enableEditing: false,
    state: {
      isLoading,
    },
  });

  let validityPeriodLabel = '—';
  if (selectedContract) {
    validityPeriodLabel = `${format(new Date(selectedContract.startDate), 'dd-MMM-yyyy')} to ${format(new Date(selectedContract.endDate), 'dd-MMM-yyyy')}`;
  } else if (selectedSP) {
    validityPeriodLabel = 'No contracts';
  }

  return (
    <SettingsMain
      title='Discounts Mapping'
      subtitle='Configure discounts mapping'
      backPath='/settings'
      topActions={
        <>
          {/* Service Provider dropdown */}
          <div className='relative'>
            <button
              type='button'
              onClick={(e) => setSpMenuEl(e.currentTarget)}
              className={clsx(
                'relative flex flex-col justify-center h-40 w-[200px] xl:w-[220px] px-8 xl:px-12 rounded-8 border',
                openSPMenu ? 'border-primary' : 'border-[#DFDFDF]'
              )}
            >
              <div className='flex items-center gap-12 w-full'>
                <span className='text-12 xl:text-14 flex-1 text-left'>{selectedSP?.label ?? 'Select...'}</span>
                <div className={clsx('ml-auto w-[20px] h-[20px] flex items-center justify-center transition-all duration-300', openSPMenu && 'rotate-180')}>
                  <img src={IconDown} alt='' />
                </div>
              </div>
              <div className='py-4 px-8 bg-white text-10 xl:text-12 text-[#9F9F9F] absolute left-8 -top-12'>Service Provider</div>
            </button>
            <StyledPopover
              open={openSPMenu}
              anchorEl={spMenuEl}
              onClose={() => setSpMenuEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              sx={{ '& .MuiPopover-paper': { width: '220px' } }}
            >
              <div className='w-full border border-[#dfdfdf] rounded-8 bg-white py-6 flex flex-col'>
                {spOptions.map((opt) => (
                  <button
                    key={opt.id}
                    type='button'
                    onClick={() => {
                      setSelectedSP({ id: opt.id, label: opt.label });
                      setSpMenuEl(null);
                    }}
                    className={clsx(
                      'px-12 xl:px-16 py-8 text-12 xl:text-14 text-left hover:bg-[#F5F6FA]',
                      selectedSP?.id === opt.id && 'bg-[#F5F6FA] font-medium'
                    )}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </StyledPopover>
          </div>

          {/* Validity Period dropdown */}
          <div className='relative'>
            <button
              type='button'
              onClick={(e) => setValidityMenuEl(e.currentTarget)}
              className={clsx(
                'relative flex flex-col justify-center h-40 w-[240px] xl:w-[260px] px-8 xl:px-12 rounded-8 border',
                openValidityMenu ? 'border-primary' : 'border-[#DFDFDF]'
              )}
            >
              <div className='flex items-center gap-12 w-full'>
                <span className='text-12 xl:text-14 flex-1 text-left'>{validityPeriodLabel}</span>
                <div
                  className={clsx('ml-auto w-[20px] h-[20px] flex items-center justify-center transition-all duration-300', openValidityMenu && 'rotate-180')}
                >
                  <img src={IconDown} alt='' />
                </div>
              </div>
              <div className='py-4 px-8 bg-white text-10 xl:text-12 text-[#9F9F9F] absolute left-8 -top-12'>Validity Period</div>
            </button>
            <StyledPopover
              open={openValidityMenu}
              anchorEl={validityMenuEl}
              onClose={() => setValidityMenuEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              <div className='w-full border border-[#dfdfdf] rounded-8 bg-white py-6 flex flex-col'>
                {contracts.length === 0 ? (
                  <div className='text-12 xl:text-14 text-[#9F9F9F] px-12 xl:px-16 py-8'>
                    {selectedSP ? 'No contracts available' : 'Select a service provider first'}
                  </div>
                ) : (
                  contracts.map((c) => (
                    <button
                      key={c.id}
                      type='button'
                      onClick={() => {
                        setSelectedContract(c);
                        setValidityMenuEl(null);
                      }}
                      className={clsx(
                        'px-12 xl:px-16 py-8 text-12 xl:text-14 text-left hover:bg-[#F5F6FA]',
                        selectedContract?.id === c.id && 'bg-[#F5F6FA] font-medium'
                      )}
                    >
                      {format(new Date(c.startDate), 'dd-MMM-yyyy')} to {format(new Date(c.endDate), 'dd-MMM-yyyy')}
                    </button>
                  ))
                )}
              </div>
            </StyledPopover>
          </div>
        </>
      }
    >
      <SettingsBaseTable table={table} totalItems={discountsTotal} pagination={pagination} onChangePage={onChangePage} onChangePageSize={onChangePageSize} />
    </SettingsMain>
  );
};
